import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes } from '../../../../primary-record-types';
import getDateString from '../../../../utils/getDateString';
import {
    allergyStatusMessages,
    conditionStatusMessages,
    pluralResourceCategoryMessages,
} from '../../../../utils/sharedMessages';
import FuzzySearch from 'fuzzy-search';
import { getIntl } from '../../../../utils/intl';
import { FilterAllergyStatus, ResourceSearchScreenParams } from '../../../../utils/appNavigationNames';
import { GroupedResource } from '../../../../hooks/api/useMedicalProfileData';
import * as Filtering from '../../../shared/Filtering';
import FilterButton from '../../Filtering/FilterButton';
import _isEqual from 'lodash/isEqual';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { getNonVisitResourceTitle } from '../../../../utils/resourceUtils/getResourceTitle';
export { getFilterSummary } from './getFilterSummary';

const intl = getIntl('resource-list-search-filters', {
    placeholder: '{category}',
    conditionStatus: 'Condition Status',
    allergyStatus: 'Allergy Status',
    medicationIsActive: 'Is Active?',
    visitType: 'Visit Type',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    routine: 'Routine',
    'walk-in': 'Walk-In',
    checkup: 'Checkup',
    followup: 'Followup',
    emergency: 'Emergency',
    other: 'Other',
    when: 'When',
    'next-month': 'Next Month',
    upcoming: 'All Upcoming',
    past: 'Past',
    filterName: 'Filter {category}',
    sortName: 'Sort {category}',
    oldest: 'Oldest',
    newest: 'Newest',
    mostRecentFirst: 'Most Recent First',
    oldestFirst: 'Oldest First',
    name: 'Name',
    aToZ: 'A-Z',
    zToA: 'Z-A',
    date: 'Date',
});

export const visitWindowFilters = ['next-month', 'upcoming', 'past', 'all'] as const;
export type VisitWindowFilter = (typeof visitWindowFilters)[number];
export const UPCOMING_WINDOW_DAYS = 31;

export type AlphaSortFilter = 'a-z' | 'z-a';
export type DateSortFilter = 'newest' | 'oldest';
export type ResourceListSortFilter = AlphaSortFilter | DateSortFilter;

export type ResourceListSearchFiltersOwnProps = {
    value: SearchFilterValue;
    onChange: (value: SearchFilterValue) => void;
    defaultSearchFilterValue: SearchFilterValue;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    allResources: SearchInputRecord[];
    disableDetailedSearch: boolean;
};

export type SearchFilterValue = {
    searchText: string;
    conditionStatus: MedicalDataTypes.PrimaryRecordConditionStatus | null;
    allergyStatus: FilterAllergyStatus | null;
    medicationIsActive: boolean | null;
    visitStatus: MedicalDataTypes.PrimaryRecordVisitType | 'other' | 'all' | null;
    /** Visits only: time window over the list. Searching bypasses the default
     * 'next-month' so a match is never hidden. */
    visitWindow: VisitWindowFilter | null;
    sort: ResourceListSortFilter;
};

/**
 * Converts the filter value to screen params so that search state can be persisted in the URL or sent
 * to other screens.
 */
export const filterValueToScreenParams = (
    filterValue: SearchFilterValue,
    category: MedicalDataTypes.PrimaryRecordResourceCategory,
): ResourceSearchScreenParams => {
    const returnValue: ResourceSearchScreenParams = {
        searchText: filterValue.searchText || undefined,
    };

    if (category === 'condition') {
        returnValue.conditionStatus = filterValue.conditionStatus || undefined;
    } else if (category === 'allergy') {
        returnValue.allergyStatus = filterValue.allergyStatus || undefined;
    } else if (category === 'medication') {
        returnValue.medicationStatus =
            typeof filterValue.medicationIsActive === 'boolean'
                ? filterValue.medicationIsActive
                    ? 'active'
                    : 'inactive'
                : undefined;
    } else if (category === 'visit') {
        returnValue.visitStatus = filterValue.visitStatus || undefined;
        returnValue.visitWindow = filterValue.visitWindow || undefined;
    }

    return returnValue;
};

/**
 * Converts the screen params to a filter value so that search state can be initialized from the URL
 */
export const screenParamsToFilterValue = (screenParams: ResourceSearchScreenParams): SearchFilterValue => ({
    searchText: screenParams.searchText || '',
    conditionStatus: screenParams.conditionStatus || null,
    allergyStatus: screenParams.allergyStatus || null,
    medicationIsActive:
        screenParams.medicationStatus === 'active' ? true : screenParams.medicationStatus === 'inactive' ? false : null,
    visitStatus: screenParams.visitStatus || null,
    visitWindow: screenParams.visitWindow || null,
    sort: 'newest',
});

export type SearchInputRecord = APITypes.medicalDataService.ListResourceResponseItem<
    | MedicalDataTypes.PrimaryRecordCondition
    | MedicalDataTypes.PrimaryRecordAllergy
    | MedicalDataTypes.PrimaryRecordMedication
    | MedicalDataTypes.PrimaryRecordVisit
    | MedicalDataTypes.PrimaryRecordVaccination
    | MedicalDataTypes.PrimaryRecordProcedure
    | MedicalDataTypes.PrimaryRecordImaging
    | MedicalDataTypes.PrimaryRecordLab
>;

const searchProps: (keyof SearchInputRecord)[] = [
    'name',
    'notes',
    'dateAdded',
    'status' as any,
    'endDateTime',
    'startDateTime',
    'dateTime',
    'vaccineType',
    'reason',
    'providerName',
    'address1',
    'address2',
    'city',
];

const formatResource = (resource: SearchInputRecord): SearchInputRecord => {
    if (resource.type === 'condition') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            endDateTime: resource.endDateTime ? getDateString(resource.endDateTime, 'date') : undefined,
            startDateTime: resource.startDateTime ? getDateString(resource.startDateTime, 'date') : undefined,
            status: resource.status ? (conditionStatusMessages(resource.status) as any) : undefined,
        };
    } else if (resource.type === 'allergy') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            endDateTime: resource.endDateTime ? getDateString(resource.endDateTime, 'date') : undefined,
            startDateTime: resource.startDateTime ? getDateString(resource.startDateTime, 'date') : undefined,
            status: resource.status ? (allergyStatusMessages(resource.status) as any) : undefined,
        };
    } else if (resource.type === 'medication') {
        return {
            ...resource,
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            endDateTime: resource.endDateTime ? getDateString(resource.endDateTime, 'date') : undefined,
            startDateTime: resource.startDateTime ? getDateString(resource.startDateTime, 'date') : undefined,
            ...({ status: resource.isActive ? intl('active') : intl('inactive') } as any), // derive a status
        };
    } else if (resource.type === 'visit') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            endDateTime: resource.endDateTime ? getDateString(resource.endDateTime, 'date') : undefined,
            startDateTime: resource.startDateTime ? getDateString(resource.startDateTime, 'date') : undefined,
        };
    } else if (resource.type === 'imaging') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            dateTime: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        };
    } else if (resource.type === 'procedure') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            dateTime: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        };
    } else if (resource.type === 'vaccination') {
        return {
            ...resource,
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            dateTime: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
            ...({ vaccineType: resource.vaccineType?.display ? resource.vaccineType.display : undefined } as any),
        };
    } else if (resource.type === 'lab') {
        return {
            ...resource,
            // @ts-expect-error TS2322
            dateAdded: resource.dateAdded ? getDateString(resource.dateAdded, 'date') : undefined,
            dateTime: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        };
    } else {
        return resource;
    }
};

export const getDefaultSearchFilterValue = (detailedSearchEnabled: boolean): SearchFilterValue => {
    if (detailedSearchEnabled) {
        return {
            searchText: '',
            conditionStatus: 'active',
            allergyStatus: 'active',
            medicationIsActive: true,
            visitStatus: 'all',
            visitWindow: 'next-month',
            sort: 'newest',
        };
    } else {
        return {
            searchText: '',
            conditionStatus: null,
            allergyStatus: null,
            medicationIsActive: null,
            visitStatus: 'all',
            // Simple lists (related resources, care team tabs) have no window control -
            // never silently hide far-future visits there.
            visitWindow: 'all',
            sort: 'newest',
        };
    }
};

export const applySearchFilter = <TRecord extends SearchInputRecord | GroupedResource>(
    input: TRecord[],
    filterValue: SearchFilterValue,
    category: MedicalDataTypes.PrimaryRecordResourceCategory,
): TRecord[] => {
    let returnList = input.map((_) => ({
        original: _,
        formatted: formatResource(_),
    }));

    if (filterValue.searchText) {
        const searcher = new FuzzySearch(
            returnList,
            searchProps.map((_) => `formatted.${_}`),
        );
        returnList = searcher.search(filterValue.searchText);
    }

    if (category === 'condition') {
        returnList = returnList.filter((_) => {
            if (filterValue.conditionStatus === null) return true;
            if (_.original.type !== 'condition') return true;

            if (filterValue.conditionStatus === 'active') {
                return _.original.status === 'active' || _.original.status === 'chronic';
            } else if (filterValue.conditionStatus === 'inactive') {
                return _.original.status === 'inactive' || _.original.status === 'ruled-out';
            }
        });
    } else if (category === 'allergy') {
        returnList = returnList.filter((_) => {
            if (filterValue.allergyStatus === null) return true;
            if (_.original.type !== 'allergy') return true;

            if (filterValue.allergyStatus === 'active') {
                return _.original.status === 'active';
            } else if (filterValue.allergyStatus === 'inactive') {
                return (
                    _.original.status === 'chronic' ||
                    _.original.status === 'inactive' ||
                    _.original.status === 'ruled-out'
                );
            }
        });
    } else if (category === 'medication') {
        returnList = returnList.filter((_) => {
            if (filterValue.medicationIsActive === null) return true;
            if (_.original.type !== 'medication') return true;
            return _.original.isActive === filterValue.medicationIsActive;
        });
    } else if (category === 'visit') {
        returnList = returnList.filter((_) => {
            if (filterValue.visitStatus === null) return true;
            if (_.original.type !== 'visit') return true;

            if (filterValue.visitStatus === 'all') {
                return true;
            } else if (filterValue.visitStatus === 'other') {
                // "Other" covers user-entered Other visits AND portal imports with no type.
                return _.original.visitType === 'other' || !_.original.visitType;
            } else {
                return _.original.visitType === filterValue.visitStatus;
            }
        });

        const window = filterValue.visitWindow || 'next-month';
        // The default window never hides a search match; explicit choices always apply.
        const applyWindow = window !== 'all' && !(window === 'next-month' && filterValue.searchText);
        if (applyWindow) {
            const now = Date.now();
            const windowEnd = now + UPCOMING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
            returnList = returnList.filter((_) => {
                if (_.original.type !== 'visit') return true;
                // Undated visits belong to no time bucket - keep them in the default view
                // (and All), exclude them from the explicit Past/Upcoming buckets.
                if (!_.original.startDateTime) return window === 'next-month';
                const start = new Date(_.original.startDateTime).getTime();
                if (window === 'next-month') return start < now || start <= windowEnd;
                if (window === 'upcoming') return start >= now;
                return start < now;
            });
        }
    }

    const getResourceName = (resource: SearchInputRecord) => {
        if (resource.type === 'visit') {
            // we don't sort by visit name, so this isn't really needed
            return resource.visitType || '';
        } else {
            return getNonVisitResourceTitle(resource);
        }
    };

    const getResourceDate = (resource: SearchInputRecord) => {
        if (
            resource.type === 'condition' ||
            resource.type === 'allergy' ||
            resource.type === 'medication' ||
            resource.type === 'visit'
        ) {
            return new Date(resource.startDateTime || new Date(0).toISOString());
        } else {
            return new Date(resource.dateTime || new Date(0).toISOString());
        }
    };

    return returnList
        .map((_) => _.original)
        .sort((a, b) => {
            if (filterValue.sort === 'newest') {
                if (category === 'visit') {
                    // Upcoming visits read soonest-first (ascending) while past visits read most-recent-first
                    // (descending), with all upcoming visits ordered ahead of past ones. Other categories keep
                    // the plain newest-first ordering.
                    const now = Date.now();
                    const aTime = getResourceDate(a).getTime();
                    const bTime = getResourceDate(b).getTime();
                    const aUpcoming = aTime >= now;
                    const bUpcoming = bTime >= now;
                    if (aUpcoming !== bUpcoming) {
                        return aUpcoming ? -1 : 1;
                    }
                    return aUpcoming ? aTime - bTime : bTime - aTime;
                }
                return getResourceDate(b).getTime() - getResourceDate(a).getTime();
            } else if (filterValue.sort === 'oldest') {
                return getResourceDate(a).getTime() - getResourceDate(b).getTime();
            } else if (filterValue.sort === 'a-z') {
                return getResourceName(a).localeCompare(getResourceName(b));
            } else if (filterValue.sort === 'z-a') {
                return getResourceName(b).localeCompare(getResourceName(a));
            } else {
                return 0;
            }
        });
};

export const useApplySearchFilter = <TRecord extends SearchInputRecord | GroupedResource>(
    input: TRecord[],
    filterValue: SearchFilterValue,
    category: MedicalDataTypes.PrimaryRecordResourceCategory,
): TRecord[] => {
    return useMemo(() => applySearchFilter(input, filterValue, category), [input, filterValue, category]);
};

export type ResourceListSearchFiltersProps = ResourceListSearchFiltersOwnProps;

const resourcesWithoutFilters: MedicalDataTypes.PrimaryRecordResourceCategory[] = [
    'vaccination',
    'procedure',
    'imaging',
];
const ResourceListSearchFilters: React.FC<ResourceListSearchFiltersProps> = (props) => {
    const { value, onChange, category } = props;

    return (
        <>
            <Filtering.Container
                renderSearch={() => (
                    <Filtering.SearchBar
                        value={value.searchText}
                        onChange={(searchText) =>
                            onChange({
                                ...value,
                                searchText,
                            })
                        }
                        testID="resource-list-search-input"
                        placeholder={intl('placeholder', { category: pluralResourceCategoryMessages(category) })}
                        isDisabled={!props.allResources.length}
                    />
                )}
                renderFilterButton={() => (
                    <Stack horizontal space={2}>
                        {resourcesWithoutFilters.includes(category) || props.disableDetailedSearch ? null : (
                            <FilterButton
                                filterPromptTitle={intl('filterName', {
                                    category: pluralResourceCategoryMessages(category),
                                })}
                                onResetFilters={() => props.onChange(props.defaultSearchFilterValue)}
                                renderFilterContents={() => (
                                    <Filtering.FilterPopoverContent
                                        resetDisabled={_isEqual(props.defaultSearchFilterValue, props.value)}
                                    >
                                        <ResourceListPrimaryRecordSearchFilters
                                            value={value}
                                            onChange={onChange}
                                            category={props.category}
                                            allResources={props.allResources}
                                            disableDetailedSearch={props.disableDetailedSearch}
                                            defaultSearchFilterValue={props.defaultSearchFilterValue}
                                        />
                                    </Filtering.FilterPopoverContent>
                                )}
                            />
                        )}
                        {props.disableDetailedSearch ? null : (
                            <FilterButton
                                buttonIcon="VerticalArrows"
                                buttonTestID="filter-sort-button"
                                filterPromptTitle={intl('sortName', {
                                    category: pluralResourceCategoryMessages(category),
                                })}
                                onResetFilters={() => onChange({ ...value, sort: 'newest' })}
                                popoverTitle={intl('sortName', {
                                    category: pluralResourceCategoryMessages(category),
                                })}
                                renderFilterContents={() => (
                                    <Filtering.FilterPopoverContent resetDisabled={value.sort === 'newest'}>
                                        <ResourceListPrimaryRecordSortFilters
                                            value={value}
                                            onChange={onChange}
                                            category={props.category}
                                            allResources={props.allResources}
                                            disableDetailedSearch={props.disableDetailedSearch}
                                            defaultSearchFilterValue={props.defaultSearchFilterValue}
                                        />
                                    </Filtering.FilterPopoverContent>
                                )}
                            />
                        )}
                    </Stack>
                )}
            />
        </>
    );
};

export const ResourceListPrimaryRecordSearchFilters: React.FC<ResourceListSearchFiltersProps> = React.memo((props) => {
    if (props.disableDetailedSearch) return null;

    if (props.category === 'condition') {
        const conditionStatuses: MedicalDataTypes.PrimaryRecordConditionStatus[] = ['active', 'inactive'];
        return (
            <Filtering.SelectFilter
                value={props.value.conditionStatus}
                onChange={(conditionStatus) => {
                    props.onChange({
                        ...props.value,
                        conditionStatus: props.value.conditionStatus === conditionStatus ? null : conditionStatus,
                    });
                }}
                options={conditionStatuses.map((status) => ({
                    value: status,
                    label: conditionStatusMessages(status),
                    count: applySearchFilter(
                        props.allResources,
                        { ...props.value, conditionStatus: status },
                        props.category,
                    ).length,
                }))}
                onClear={() => props.value.conditionStatus && props.onChange({ ...props.value, conditionStatus: null })}
                title={intl('conditionStatus')}
            />
        );
    } else if (props.category === 'allergy') {
        const filterAllergyStatus: FilterAllergyStatus[] = ['active', 'inactive'];
        return (
            <Filtering.SelectFilter
                onClear={
                    props.value.allergyStatus
                        ? () => props.onChange({ ...props.value, allergyStatus: null })
                        : undefined
                }
                title={intl('allergyStatus')}
                value={props.value.allergyStatus}
                options={filterAllergyStatus.map((status) => ({
                    label: conditionStatusMessages(status),
                    count: applySearchFilter(
                        props.allResources,
                        { ...props.value, allergyStatus: status },
                        props.category,
                    ).length,
                    value: status,
                }))}
                onChange={(conditionStatus) => {
                    const asStatus = conditionStatus as FilterAllergyStatus;
                    props.onChange({
                        ...props.value,
                        allergyStatus: props.value.allergyStatus === asStatus ? null : asStatus,
                    });
                }}
            />
        );
    } else if (props.category === 'medication') {
        const medicationStatuses = ['active', 'inactive'] as const;
        const value: 'active' | 'inactive' | null =
            props.value.medicationIsActive === true
                ? 'active'
                : props.value.medicationIsActive === false
                ? 'inactive'
                : null;
        return (
            <Filtering.SelectFilter
                onClear={
                    props.value.medicationIsActive !== null
                        ? () => props.onChange({ ...props.value, medicationIsActive: null })
                        : undefined
                }
                title={intl('medicationIsActive')}
                value={value}
                options={medicationStatuses.map((status) => ({
                    label: intl(status),
                    count: applySearchFilter(
                        props.allResources,
                        { ...props.value, medicationIsActive: status === 'active' ? true : false },
                        props.category,
                    ).length,
                    value: status,
                }))}
                onChange={(medicationStatus) => {
                    const newStatus = medicationStatus === 'active' ? true : false;
                    props.onChange({
                        ...props.value,
                        medicationIsActive: newStatus === props.value.medicationIsActive ? null : newStatus,
                    });
                }}
            />
        );
    } else if (props.category === 'visit') {
        const visitTypes: (MedicalDataTypes.PrimaryRecordVisitType | 'all' | 'other')[] = [
            'all',
            'routine',
            'walk-in',
            'checkup',
            'followup',
            'emergency',
            'other',
        ];
        return (
            <>
                <Filtering.SelectFilter
                    onClear={
                        props.value.visitWindow === 'next-month' || !props.value.visitWindow
                            ? undefined
                            : () => props.onChange({ ...props.value, visitWindow: 'next-month' })
                    }
                    title={intl('when')}
                    value={props.value.visitWindow || 'next-month'}
                    options={visitWindowFilters.map((window) => ({
                        label: intl(window),
                        count: applySearchFilter(
                            props.allResources,
                            { ...props.value, visitWindow: window },
                            props.category,
                        ).length,
                        value: window,
                    }))}
                    onChange={(window) => {
                        props.onChange({
                            ...props.value,
                            visitWindow: window,
                        });
                    }}
                />
                <Filtering.SelectFilter
                    onClear={
                        props.value.visitStatus === 'all'
                            ? undefined
                            : () => props.onChange({ ...props.value, visitStatus: 'all' })
                    }
                    title={intl('visitType')}
                    value={props.value.visitStatus}
                    options={visitTypes.map((type) => ({
                        label: intl(type),
                        count: applySearchFilter(
                            props.allResources,
                            { ...props.value, visitStatus: type },
                            props.category,
                        ).length,
                        value: type,
                    }))}
                    onChange={(visitType) => {
                        props.onChange({
                            ...props.value,
                            visitStatus: visitType,
                        });
                    }}
                />
            </>
        );
    } else {
        return null;
    }
});

export const ResourceListPrimaryRecordSortFilters: React.FC<ResourceListSearchFiltersProps> = React.memo((props) => {
    if (props.disableDetailedSearch) return null;

    const shouldIncludeNameSort =
        props.category !== 'vaccination' && props.category !== 'procedure' && props.category !== 'visit';

    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            <Filtering.SelectFilter
                onClear={() => props.onChange({ ...props.value, sort: 'newest' })}
                title={intl('date')}
                value={props.value.sort === 'newest' || props.value.sort === 'oldest' ? props.value.sort : null}
                options={[
                    {
                        label: intl('newest'),
                        value: 'newest',
                    },
                    {
                        label: intl('oldest'),
                        value: 'oldest',
                    },
                ]}
                onChange={(sort) => {
                    if (sort) {
                        props.onChange({
                            ...props.value,
                            sort,
                        });
                    }
                }}
            />
            {shouldIncludeNameSort ? (
                <Filtering.SelectFilter
                    onClear={() => props.onChange({ ...props.value, sort: 'newest' })}
                    title={intl('name')}
                    value={props.value.sort === 'a-z' || props.value.sort === 'z-a' ? props.value.sort : null}
                    options={[
                        {
                            label: intl('aToZ'),
                            value: 'a-z',
                        },
                        {
                            label: intl('zToA'),
                            value: 'z-a',
                        },
                    ]}
                    onChange={(sort) => {
                        if (sort) {
                            props.onChange({
                                ...props.value,
                                sort,
                            });
                        }
                    }}
                />
            ) : null}
        </Stack>
    );
});

export default React.memo(ResourceListSearchFilters);
